/**
 * `saveExtension` — debounced autosave + dirty-state tracking + the D-007 save path
 * (editor-chat.md §2.1, §2.5, §5 step 3).
 *
 * editor-chat.md §2.5: the editor calls `onSave(body)` → `PUT /api/v1/notes/{id}` /
 * `update_note`. The backend sets `locallyModifiedAt` when `source='joplin'` and the body
 * actually changed (D-007) and returns a `SaveResult` with the dirty/conflict flags. The
 * editor *reflects* `conflicted` in its status affordance and links to Corpus Health — it does
 * **not** own the state machine (editor-chat.md §1.6).
 *
 * editor-chat.md §4: a failed save is an **external error** — retry with backoff, then surface
 * inline "couldn't save — retrying / failed"; **never lose the buffer** (keep it dirty). This
 * extension owns:
 *  - a dirty-state tracker: an `updateListener` that flags the buffer dirty on a real doc
 *    change vs the last saved body;
 *  - a debounced autosave: after `AUTOSAVE_DEBOUNCE_MS` of quiet, save;
 *  - a save keymap: `Mod-s` saves immediately (bypassing the debounce);
 *  - the save-result plumbing: it reports every `EditorSaveState` transition through an
 *    injected callback so the surface's status affordance can render it.
 *
 * It does NOT issue the network call itself — `onSave` is injected (the surface owns the
 * `update_note` call, so the editor stays decoupled from the API layer).
 */

import { EditorView, keymap } from '@codemirror/view';
import { StateEffect, StateField, type Extension } from '@codemirror/state';
import { getLogger } from '@/api';
import type { EditorSaveFn, EditorSaveState } from '../editorTypes';

const log = getLogger('editor.save');

/** Quiet-period before a debounced autosave fires. Tuned so a steady typist saves between pauses. */
const AUTOSAVE_DEBOUNCE_MS = 1500;

/**
 * A `StateEffect` that carries a save-state transition into the editor state — dispatched by
 * the save controller so the dirty/saving/saved/conflicted state lives *in* the editor state
 * (and a future status-bar widget could read it as a `StateField`).
 */
const setSaveStateEffect = StateEffect.define<EditorSaveState>();

/**
 * A `StateField` holding the editor's current `EditorSaveState` — updated by
 * `setSaveStateEffect`. Kept in editor state (not just React state) so the dirty/conflict
 * status is part of the editor's own model; the surface mirrors it via the injected callback
 * for its status affordance.
 */
const saveStateField = StateField.define<EditorSaveState>({
  create: () => ({ status: 'clean' }),
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setSaveStateEffect)) return effect.value;
    }
    return value;
  },
});

/**
 * The save controller — owns the debounce timer, the last-saved snapshot, and the actual save
 * sequence. One instance per editor, captured by the extension's listeners.
 *
 * The "last saved body" snapshot is the byte-comparison baseline (editor-chat.md §1.2 — the
 * body the editor saves is the body the extractor sees; dirty detection is a faithful body
 * comparison, no normalization).
 */
class SaveController {
  private lastSavedBody: string;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Guards against an overlapping save — a save requested while one is in flight is queued. */
  private saving = false;
  private saveQueued = false;
  private disposed = false;

  constructor(
    initialBody: string,
    private readonly onSave: EditorSaveFn,
    private readonly onStateChange: (state: EditorSaveState, view: EditorView) => void,
  ) {
    this.lastSavedBody = initialBody;
  }

  /** True iff `body` differs from the last successfully-saved body — the dirty predicate. */
  isDirty(body: string): boolean {
    return body !== this.lastSavedBody;
  }

  /**
   * The buffer changed — flag dirty (if it genuinely differs from the saved body) and (re)arm
   * the debounced autosave. Called from the `updateListener` on every doc change.
   */
  noteDocChanged(view: EditorView): void {
    if (this.disposed) return;
    const body = view.state.doc.toString();
    if (!this.isDirty(body)) {
      // The edit brought the buffer back to the saved body (e.g. an undo) — it is clean again.
      this.transition({ status: 'clean' }, view);
      if (this.debounceTimer !== null) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      return;
    }
    this.transition({ status: 'dirty' }, view);
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.save(view);
    }, AUTOSAVE_DEBOUNCE_MS);
  }

  /**
   * Save now — bypasses the debounce (the `Mod-s` keymap path, and the debounce-fire path).
   * A save while one is in flight queues exactly one follow-up so the latest buffer wins.
   */
  async save(view: EditorView): Promise<void> {
    if (this.disposed) return;
    const body = view.state.doc.toString();
    if (!this.isDirty(body)) return; // nothing to save — the buffer matches the saved body
    if (this.saving) {
      this.saveQueued = true;
      return;
    }
    this.saving = true;
    this.transition({ status: 'saving' }, view);
    try {
      const result = await this.onSave(body);
      if (this.disposed) return;
      // The save succeeded — this body is now the baseline. Note `result.conflicted` is D-007:
      // ingestion detected a both-sides edit. The editor *displays* it (a distinct state) and
      // links to Corpus Health; it does NOT resolve the conflict (editor-chat.md §1.6 / §2.5).
      this.lastSavedBody = body;
      if (result.conflicted) {
        log.warn('editor_save_conflicted', { noteId: result.noteId });
        this.transition({ status: 'conflicted', result }, view);
      } else {
        log.debug('editor_save_ok', {
          noteId: result.noteId,
          // `locallyModifiedAt` non-null == a Joplin note was dirtied server-side (D-007).
          dirtiedJoplinNote: result.locallyModifiedAt !== null,
        });
        this.transition({ status: 'saved', result }, view);
      }
    } catch (err) {
      if (this.disposed) return;
      // A failed save is an EXTERNAL error (editor-chat.md §4): keep the buffer dirty (the
      // baseline is unchanged — `lastSavedBody` was NOT updated), surface it, let the user
      // re-issue. Never lose the buffer.
      const message = err instanceof Error ? err.message : 'The note could not be saved.';
      const retryable = isRetryableSaveError(err);
      log.error('editor_save_failed', { error: message, retryable });
      this.transition({ status: 'save-failed', message, retryable }, view);
    } finally {
      this.saving = false;
      if (this.saveQueued && !this.disposed) {
        this.saveQueued = false;
        // The buffer moved on while we saved — save again so the latest content lands.
        void this.save(view);
      }
    }
  }

  /** Tear down — clears the debounce timer. Called on editor unmount.
   *
   * Phase 4 of the 2026-05-18 extraction-remediation plan — if a debounced save is
   * still pending (the user typed within the last AUTOSAVE_DEBOUNCE_MS and is now
   * navigating away), fire the save BEFORE marking disposed so the network call lands
   * even though we won't be alive to receive its response. The backend's
   * adapters.py::update_note enqueues Stage A1 on every save, which cascades to A2 +
   * Stage C — so a flushed-on-unmount save also triggers the reclassify/embed/etc
   * re-extract the user expects after every edit.
   *
   * Fire-and-forget by design — we can't await this through React's unmount; the
   * fetch is sent before the network connection tears down, and the `disposed` flag
   * is set AFTER so the save() body's `if (this.disposed)` guards still let the
   * network call go out (the guards only short-circuit the post-response state
   * transitions, which would have nowhere to land anyway).
   */
  dispose(view?: EditorView): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      // A pending debounced save was queued — flush it now so the user's last edit
      // lands. Only attempt the flush when we have a view (the editor lifecycle
      // does pass it through `destroy`).
      if (view !== undefined) {
        const body = view.state.doc.toString();
        if (this.isDirty(body)) {
          // Fire and forget — we won't be alive for the response, but the network
          // POST is already in flight by the time we set disposed below. The
          // backend's atomic upsert + Stage A1 enqueue lands regardless.
          void this.onSave(body).catch((err) => {
            // Best-effort: log but don't surface — the editor is already unmounting.
            log.warn('editor_save_flush_on_unmount_failed', {
              error: err instanceof Error ? err.message : String(err),
            });
          });
          this.lastSavedBody = body;
        }
      }
    }
    this.disposed = true;
  }

  /** Push a save-state transition both into the editor state and out to the surface callback. */
  private transition(state: EditorSaveState, view: EditorView): void {
    view.dispatch({ effects: setSaveStateEffect.of(state) });
    this.onStateChange(state, view);
  }
}

/**
 * Whether a thrown save error is the kind the user can sensibly retry. The injected `onSave`
 * surfaces `AletheiaApiError` with an `envelope.retryable` flag (the backend's stable-code
 * table); a plain error defaults to retryable (a transient transport failure is the common
 * case, and "retry" is the safe affordance — the backend owns the actual retry, conventions.md
 * §7, and re-issuing a `PUT` is idempotent).
 */
function isRetryableSaveError(err: unknown): boolean {
  if (err !== null && typeof err === 'object' && 'envelope' in err) {
    const envelope = (err as { envelope?: { retryable?: boolean } }).envelope;
    if (envelope !== undefined && typeof envelope.retryable === 'boolean') {
      return envelope.retryable;
    }
  }
  return true;
}

/** What `saveExtension` returns — the CM6 `Extension` plus a teardown thunk for the controller. */
export interface SaveExtensionBundle {
  /** The composed CM6 extension — the save-state field, the dirty listener, the save keymap. */
  extension: Extension;
  /** Imperatively trigger a save (the surface's explicit "Save" affordance can call this). */
  saveNow: (view: EditorView) => void;
  /**
   * Tear the controller down — the surface calls this on editor unmount.
   *
   * Phase 4 of the 2026-05-18 extraction-remediation plan: passing the live view
   * lets the controller flush a pending dirty save before disposing, so a
   * navigate-away with unsaved typing still triggers the backend's
   * Stage A1 enqueue (which cascades to A2 + Stage C — the user-visible
   * "reclassify/embed on edit" behavior). Calling without a view preserves the
   * old "just clear the timer" behavior for paths that genuinely have no view.
   */
  dispose: (view?: EditorView) => void;
}

/**
 * Build the save extension — debounced autosave + dirty tracking + the `Mod-s` keymap, over an
 * injected `onSave` and a save-state callback (editor-chat.md §2.1, §2.5).
 *
 * @param initialBody - The note's body at mount — the dirty-comparison baseline. The editor is
 *   a faithful text surface, so this is the verbatim `get_note` body (editor-chat.md §1.2).
 * @param onSave - The injected save callback — issues `update_note` / `PUT /notes/{id}`. The
 *   surface owns the network call; a thrown error is treated as an external error.
 * @param onStateChange - Invoked on every `EditorSaveState` transition so the surface's status
 *   affordance (and the shell status-bar slot) can render dirty/saving/saved/conflicted.
 */
export function saveExtension(
  initialBody: string,
  onSave: EditorSaveFn,
  onStateChange: (state: EditorSaveState, view: EditorView) => void,
): SaveExtensionBundle {
  const controller = new SaveController(initialBody, onSave, onStateChange);

  const dirtyListener = EditorView.updateListener.of((update) => {
    if (update.docChanged) {
      controller.noteDocChanged(update.view);
    }
  });

  const saveKeymap = keymap.of([
    {
      key: 'Mod-s',
      // `Mod-s` saves immediately — bypasses the debounce. `preventDefault` so the browser's
      // own save dialog never appears.
      preventDefault: true,
      run: (view) => {
        void controller.save(view);
        return true;
      },
    },
  ]);

  return {
    extension: [saveStateField, dirtyListener, saveKeymap],
    saveNow: (view) => void controller.save(view),
    dispose: (view?: EditorView) => controller.dispose(view),
  };
}

/** Read the current `EditorSaveState` out of an editor view's state — for a status widget. */
export function readSaveState(view: EditorView): EditorSaveState {
  return view.state.field(saveStateField);
}
